import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { ColumnApi, GridApi, GridOptions, GridReadyEvent, RowDataUpdatedEvent, ValueFormatterParams } from 'ag-grid-community';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CustomNoRowsOverlayComponent } from '../../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { ETLReportingSetRecalculationObj } from '../cxone-etl-recalculation-modal.model';
import { CxoneEtlRecalculationService } from '../cxone-etl-recalculation.service';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { EtlCustomCellValueRendererComponent } from '../../../shared/etl-custom-cell-value-renderer/etl-custom-cell-value-renderer.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { ETLRecalculationStatus } from '../../cxone-etl-data-recalculation/cxone-etl-data-recalculation.model';
import { Subscription } from 'rxjs';

@Component({
    selector: 'cv-reporting-set-detailed-view',
    templateUrl: './reporting-set-detailed-view.component.html',
    styleUrls: ['./reporting-set-detailed-view.component.scss']
})
export class ReportingSetDetailedViewComponent implements OnInit {
    @Output() selectedReportingSetList: EventEmitter<string[]> = new EventEmitter();
    @Output() totalCountLabel: EventEmitter<string> = new EventEmitter();
    @Output() gridData: EventEmitter<GridApi> = new EventEmitter();
    @Input() reloadReportingSetGrid: EventEmitter<any> = new EventEmitter();
    @Input() searchInput = "";
    @Input() reportingSetsInProgress: ETLRecalculationStatus[];
    public selectedReportingSetDefList: string[] = [];
    public showSpinner: boolean;
    public reportingSetsGridOptions: GridOptions;
    public gridColumnApi: ColumnApi;
    public gridApi: GridApi;
    public isLocalizationSectionLoaded: boolean;
    public gridDataReportingSet: ETLReportingSetRecalculationObj[] = [];
    public subscriptions: Subscription[] = [];

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private cxoneEtlRecalculation: CxoneEtlRecalculationService,
        private toastrManager: ToastrManager
    ) { }

    async ngOnInit(): Promise<void> {
        this.showSpinner = true;
        await this.loadLocalization();
        this.isLocalizationSectionLoaded = true;
        this.initializeGrid();
        this.cxoneEtlRecalculation.reportingSetListChange$.subscribe(reportingSets => {
            if (this.cxoneEtlRecalculation.getEmittedValue === 'list-view') {
                this.gridApi?.forEachNode(node => {
                    const isSelected = reportingSets.includes(node.data._id);
                    node.setSelected(isSelected);
                });
                this.cxoneEtlRecalculation.setEmittedValue = undefined;
            }
        })

        this.subscriptions.push(this.reloadReportingSetGrid.subscribe(() => {
            this.loadDataRecalculation();
        }));
    }

    ngOnDestroy() {
        this.subscriptions?.forEach(s => s?.unsubscribe());
    }

    public preselectRows(): void {
        const lastReportingSetListChange = this.cxoneEtlRecalculation.getLastReportingSetChange();
        if (lastReportingSetListChange) {
            this.gridApi?.forEachNode(node => {
                const isSelected = lastReportingSetListChange.includes(node.data._id);
                node.setSelected(isSelected);
            });
        }
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes.searchInput && this.gridApi) {
            this.searchInput = changes.searchInput.currentValue;
            this.gridApi.onFilterChanged();
            this.manageNoRowsOverlay();
        }
    }

    public initializeGrid() {
        this.reportingSetsGridOptions = {
            columnDefs: [
                {
                    field: 'BaseFeed',
                    colId: 'BaseFeed',
                    headerName: this.getLocalizationString("Base_Feed", "Base Feed"),
                    checkboxSelection: true,
                    headerCheckboxSelection: false,
                    headerClass: ["checkBoxClass", "customFieldClass"],
                    cellClass: "customFieldCellClass",
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true,
                    },
                    resizable: true,
                    sortable: true,
                    suppressMovable: true,
                    suppressSizeToFit: true,
                    width: 350,
                },
                {
                    field: 'ReportingSetName',
                    colId: 'ReportingSetName',
                    headerName: this.getLocalizationString("Reporting_Set", "Reporting Set"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'ReportingSetGroup',
                    colId: 'ReportingSetGroup',
                    headerName: this.getLocalizationString("Category", "Category"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
            ],
            context: this,
            getRowHeight: (params) => params.node.data.editable ? 70 : 44,
            components: {
                CustomNoRowsOverlayComponent,
                cxoneETLCustomCellRenderer: EtlCustomCellValueRendererComponent
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                    this.getLocalizationString("No_Result_Found", "No result found")
                    : this.getLocalizationString("No_Reporting_Sets_Added", "No reporting sets added yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchInput !== '' ?
                        '../App_Themes/CXone/icons/no-results.svg#no-results'
                        : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                    return path;
                }
            },
            headerHeight: 45,
            suppressCellFocus: true,
            suppressRowClickSelection: true,
            scrollbarWidth: 8,
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onRowDataUpdated: (event: RowDataUpdatedEvent) => this.setRowCountLabel(),
            onFilterChanged: () => {
                this.gridApi.redrawRows();
                this.setRowCountLabel();
            },
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: (node) => {
                const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
                return result;
            },
            getRowId: ({data: rowData}) => rowData._id,
            onRowSelected: this.onRowSelected.bind(this),
            getRowStyle: this.getRowStyle.bind(this)
        };
    }

    getRowStyle(params: any) {
        let isRowDisabled = null;
        this.reportingSetsInProgress.forEach(item => {
            const reportingSets = item.definitions.split(',').map(def => def.trim());
            if (reportingSets.includes(params.data.ReportingSetName.trim())) {
                isRowDisabled = { 'pointer-events': 'none', opacity: 0.5 };
            }
        });
        return isRowDisabled;
    }

    onRowSelected(event: any) {
        if (!this.cxoneEtlRecalculation.getEmittedValue) {
            if (event.node.isSelected()) {
                this.selectedReportingSetDefList.push(event.data._id);
            } else {
                let index = this.selectedReportingSetDefList.indexOf(event.data._id);
                this.selectedReportingSetDefList.splice(index, 1);
            }
            this.cxoneEtlRecalculation.setEmittedValue = 'detailed-view';
            this.selectedReportingSetList.emit(this.selectedReportingSetDefList);
        }
    }

    public async loadDataRecalculation(): Promise<void> {
        try {
            const response = await this.cxoneEtlRecalculation.loadRecalculationReportingSetData();

            const reportingData = response.map((inputObject) => ({
                _id: inputObject._id,
                BaseFeed: inputObject.BaseFeed,
                ReportingSetName: inputObject.ReportingSetName,
                ReportingSetGroup: inputObject.ReportingSetGroup
            }));

            this.gridDataReportingSet = reportingData;
            this.setGridRecordData();
            this.preselectRows();
        } catch (error) {
            console.error('Failed to load reporting set data:', error);
            this.gridDataReportingSet = [];
            this.setGridRecordData();
            this.toastrManager.showToaster('error', this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
    }

    public setGridRecordData() {
        if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.gridDataReportingSet);
        }
        this.showSpinner = false;
        this.manageNoRowsOverlay();
        this.gridApi.sizeColumnsToFit();
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public setRowCountLabel() {
        let count = 0;
        this.gridApi.forEachNodeAfterFilter(() => {
            count++;
        });
        this.totalCountLabel.emit(this.getTotalCountText(count));
    }

    public manageNoRowsOverlay() {
        if (this.gridApi.getDisplayedRowCount() === 0)
            this.gridApi.showNoRowsOverlay();
        else
            this.gridApi.hideOverlay();
    }

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Recalculation_Modal');
        }
        catch {
            console.error('Failed to load localization for ETL_Recalculation_Modal');
        }
    }

    public onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridData.emit(this.gridApi);
        this.loadDataRecalculation();
    }

    public getTotalCountText(gridRowsCount: number): string {
        const key: string = gridRowsCount === 1 ? 'Total_Reporting_Sets_Singular' : 'Total_Reporting_Sets_Plural';
        const defaultValue: string = gridRowsCount === 1 ? '{0} reporting sets' : '{0} reporting sets';
        return this.cvLocalizationService.Format(this.getLocalizationString(key, defaultValue), gridRowsCount);
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Recalculation_Modal", key, defaultValue);
        return baseString;
    }
}
